import { getPortalConfig } from "../configurations";
import { useSessionDataStore } from "../hooks/useSessionDataStore";
import {
    getDeviceBrowser,
    getDeviceOSName,
    getDeviceType,
} from "../utils/userAgentUtils";

export interface DimensionSet {
    [key: string]: string;
}

export interface AdditionalProperties {
    [key: string]: string | number | boolean;
}

export interface EmfMetric {
    readonly name: string;
    readonly value: number;
    readonly unit: string;
    readonly dimensions?: DimensionSet;
}

export interface EmfHttpStatusCodeMetric extends EmfMetric {
    readonly httpStatusCode: number;
}

export const EMF_CONSTANTS = {
    NAMESPACE: "AWSErmineWebApp-EMF",
    DIMENSIONS: {
        BROWSER: "Browser",
        OPERATION: "Operation",
        HTTP_STATUS_CODE: "HttpStatusCode",
    },
} as const;

export const getDefaultDimensionSets = (): DimensionSet[] => {
    const browser = getDeviceBrowser();
    return [
        {
            [EMF_CONSTANTS.DIMENSIONS.BROWSER]: browser,
        },
    ];
};

export const getCommonDimensions = (): DimensionSet => ({
    [EMF_CONSTANTS.DIMENSIONS.BROWSER]: getDeviceBrowser(),
});

export const getCommonProperties = (): AdditionalProperties => ({
    PortalArn: getPortalConfig().portalArn,
    UserSub: window.userSub,
    SessionId: useSessionDataStore.getState().sessionID,
    DeviceType: getDeviceType(),
    DeviceOS: getDeviceOSName(),
});

export interface EmfLogOptions {
    namespace: string;
    dimensionSets: DimensionSet[];
    metrics: EmfMetric[];
    timestamp?: number;
    additionalProperties?: AdditionalProperties;
}

export const createEmfLog = ({
    namespace,
    dimensionSets,
    metrics,
    timestamp = Date.now(),
    additionalProperties = {},
}: EmfLogOptions): Record<string, any> => {
    const emfMetrics = metrics.map((metric) => ({
        Name: metric.name,
        ...(metric.unit && { Unit: metric.unit }),
    }));

    const metricValues = metrics.reduce<Record<string, number>>(
        (collectedValues, metric) => ({
            ...collectedValues,
            [metric.name]: metric.value,
        }),
        {}
    );

    const emfLog = {
        _aws: {
            Timestamp: timestamp,
            CloudWatchMetrics: [
                {
                    Namespace: namespace,
                    Dimensions: dimensionSets.map((set) => Object.keys(set)),
                    Metrics: emfMetrics,
                },
            ],
        },
    };

    const allDimensions = dimensionSets.reduce<DimensionSet>(
        (combinedDimensions, set) => ({
            ...combinedDimensions,
            ...set,
        }),
        {}
    );

    // https://code.amazon.com/packages/KatalLoggerCloudWatchBackend/blobs/mainline/--/README.md
    return {
        // This has to be added by the client side logger
        emfLog: {
            ...emfLog,
            ...allDimensions,
            ...metricValues,
            ...additionalProperties,
        },
    };
};

export const createEmfFormattedMetric = (metric: EmfMetric) => {
    const additionalProperties: AdditionalProperties = {
        ...getCommonProperties(),
        ...getCommonDimensions(),
    };

    return createEmfLog({
        namespace: EMF_CONSTANTS.NAMESPACE,
        dimensionSets: metric.dimensions
            ? [metric.dimensions]
            : getDefaultDimensionSets(),
        metrics: [metric],
        additionalProperties,
    });
};

export const createEmfHttpStatusCodeMetric = (
    metric: EmfHttpStatusCodeMetric
) => {
    const defaultDimensionSets: DimensionSet[] = [
        {
            [EMF_CONSTANTS.DIMENSIONS.OPERATION]: metric.name,
            [EMF_CONSTANTS.DIMENSIONS
                .HTTP_STATUS_CODE]: metric.httpStatusCode.toString(),
        },
    ];

    const additionalProperties: AdditionalProperties = {
        ...getCommonProperties(),
        ...getCommonDimensions(),
        HttpStatusCode: metric.httpStatusCode,
    };

    return createEmfLog({
        namespace: EMF_CONSTANTS.NAMESPACE,
        dimensionSets: metric.dimensions
            ? [metric.dimensions]
            : defaultDimensionSets,
        metrics: [metric],
        additionalProperties,
    });
};
